import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Copy, Download, CheckCircle, X, Eye, ArrowRight, 
  Clock, Shield, FileCheck, Zap, Building2, Code, Lock, Cloud
} from 'lucide-react';

// Shared styles - matching Dashboard.jsx design system
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
  border: '#e5e7eb'
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

// Modal Component for Contract Display
const ContractModal = ({ isOpen, onClose, contractContent, contractType, onCopy, onDownload }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)' }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-x-4 top-20 bottom-20 z-50 mx-auto max-w-4xl"
          >
            <div className="h-full bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                 style={{ fontFamily: fontStyles.fontFamily }}>
              
              {/* Modal Header */}
              <div className="px-6 py-4 border-b flex items-center justify-between" 
                   style={{ 
                     borderColor: colors.border, 
                     background: 'linear-gradient(to right, #f9fafc, #f5f7fa)'
                   }}>
                <div className="flex items-center">
                  <FileCheck size={20} className="mr-3" style={{ color: colors.accent }} />
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: colors.gray900, ...fontStyles.heading }}>
                      {contractType || 'Generated Contract'}
                    </h3>
                    <p className="text-xs mt-0.5" style={{ color: colors.gray500 }}>
                      Ready for review and signature
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={onCopy}
                    className="p-2 rounded-lg border hover:bg-gray-50 transition-all"
                    style={{ borderColor: colors.border }}
                    title="Copy to clipboard"
                  >
                    <Copy size={16} style={{ color: colors.gray600 }} />
                  </button>
                  <button 
                    onClick={onDownload}
                    className="p-2 rounded-lg border hover:bg-gray-50 transition-all"
                    style={{ borderColor: colors.border }}
                    title="Download PDF"
                  >
                    <Download size={16} style={{ color: colors.gray600 }} />
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-all ml-2"
                    title="Close"
                  >
                    <X size={20} style={{ color: colors.gray600 }} />
                  </button>
                </div>
              </div>

              {/* Contract Content */}
              <div className="flex-1 overflow-y-auto p-8" style={{ backgroundColor: '#fafbfc' }}>
                <div className="max-w-3xl mx-auto bg-white rounded-lg p-8" 
                     style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }}>
                  <pre className="whitespace-pre-wrap text-sm leading-relaxed" 
                       style={{ 
                         fontFamily: "'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif", 
                         color: colors.gray900,
                         lineHeight: '1.8'
                       }}>
                    {contractContent}
                  </pre>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t flex items-center justify-between" 
                   style={{ borderColor: colors.border, backgroundColor: colors.gray50 }}>
                <div className="flex items-center space-x-4 text-sm">
                  <div className="flex items-center" style={{ color: colors.gray600 }}>
                    <Shield size={16} className="mr-1.5" style={{ color: colors.success }} />
                    <span>Legally Binding</span>
                  </div>
                  <div className="flex items-center" style={{ color: colors.gray600 }}>
                    <Clock size={16} className="mr-1.5" style={{ color: colors.accent }} />
                    <span>Generated in 8 seconds</span>
                  </div>
                </div>
                
                <motion.button 
                  className="px-6 py-2.5 rounded-xl text-white font-medium transition-all flex items-center"
                  style={{ 
                    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                    boxShadow: '0 4px 14px rgba(30, 64, 175, 0.3)',
                    ...fontStyles.medium
                  }}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Proceed to Sign
                  <ArrowRight size={16} className="ml-2" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Professional Contract Generation Demo
export const ModernContractGenerationDemo = () => {
  const [userPrompt, setUserPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [contractContent, setContractContent] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [activeExample, setActiveExample] = useState(1);
  const [generatedContracts, setGeneratedContracts] = useState([]);
  const [currentContractType, setCurrentContractType] = useState('');

  // Pre-filled example contract
  const defaultContract = `SOFTWARE DEVELOPMENT AGREEMENT

This Software Development Agreement ("Agreement") is entered into as of ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} ("Effective Date"), by and between:

CLIENT: [Company Name], a [State] corporation
Address: [Client Address]
("Client")

DEVELOPER: [Developer Name], a [State] corporation  
Address: [Developer Address]
("Developer")

RECITALS

WHEREAS, Client desires to engage Developer to design, develop, and deliver custom software solutions as described herein; and

WHEREAS, Developer represents that it has the necessary expertise, resources, and capabilities to perform the services requested by Client;

NOW, THEREFORE, in consideration of the mutual covenants and agreements contained herein, and for other good and valuable consideration, the receipt and sufficiency of which are hereby acknowledged, the parties agree as follows:

1. SCOPE OF SERVICES

1.1 Development Services. Developer agrees to provide software development services for the following project:
    • Project Name: [Project Name]
    • Platform: Web Application / Mobile Application
    • Technology Stack: [Specify Technologies]
    • Key Features: [List Major Features]

1.2 Deliverables. Developer shall deliver the following:
    • Functional specifications and technical documentation
    • Source code with comprehensive documentation
    • User interface designs and assets
    • Testing documentation and reports
    • Deployment and configuration guides

2. PROJECT TIMELINE

2.1 Development Phases. The project shall be completed in the following phases:
    
    Phase 1: Discovery & Design (Weeks 1-2)
    • Requirements gathering and analysis
    • UI/UX design mockups and wireframes
    • Technical architecture planning
    • Deliverable: Design documentation and prototypes
    
    Phase 2: Core Development (Weeks 3-8)
    • Backend infrastructure setup
    • Database design and implementation
    • Core feature development
    • API development and integration
    
    Phase 3: Frontend & Integration (Weeks 9-10)
    • User interface implementation
    • Third-party service integrations
    • System integration testing
    
    Phase 4: Testing & Deployment (Weeks 11-12)
    • Quality assurance testing
    • User acceptance testing
    • Production deployment
    • Post-launch support setup

2.2 Milestones. Progress shall be measured by the following milestones:
    • Milestone 1: Approved designs and specifications (20% payment)
    • Milestone 2: Core functionality complete (30% payment)
    • Milestone 3: Full feature implementation (30% payment)
    • Milestone 4: Launch and acceptance (20% payment)

3. COMPENSATION

3.1 Project Fee. Client agrees to pay Developer a total project fee of $[Amount] USD.

3.2 Payment Schedule. Payments shall be made according to the milestone schedule outlined in Section 2.2.

3.3 Additional Services. Any services requested beyond the original scope shall be billed at $[Rate]/hour.

[Document continues with additional standard clauses...]`;

  // Professional contract types (no emojis)
  const contractTypes = [
    {
      id: 1,
      icon: Code,
      title: "Software Development",
      subtitle: "Custom software projects",
      prompt: "I need a software development contract for building a custom web application. Budget is $25,000, timeline is 3 months, milestone-based payments.",
      category: "Technology"
    },
    {
      id: 2,
      icon: Building2,
      title: "Service Agreement",
      subtitle: "Professional services",
      prompt: "Create a professional services agreement for consulting work, ongoing engagement at $150/hour.",
      category: "Consulting"
    },
    {
      id: 3,
      icon: Lock,
      title: "Non-Disclosure",
      subtitle: "Confidentiality agreement",
      prompt: "Generate a mutual NDA for discussing a potential business partnership and sharing proprietary information.",
      category: "Legal"
    },
    {
      id: 4,
      icon: Cloud,
      title: "SaaS Agreement",
      subtitle: "Software subscription",
      prompt: "Write a B2B SaaS agreement for cloud-based project management software, monthly subscription model.",
      category: "Technology"
    }
  ];

  // Load default on mount
  useEffect(() => {
    const firstExample = contractTypes[0];
    setUserPrompt(firstExample.prompt);
    setContractContent(defaultContract);
    setCurrentContractType(firstExample.title);
    setGeneratedContracts([{
      id: Date.now(),
      title: firstExample.title,
      Icon: firstExample.icon,
      subtitle: firstExample.subtitle,
      category: firstExample.category,
      content: defaultContract,
      timestamp: new Date()
    }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateContract = () => {
    if (!userPrompt.trim()) return;
    
    setIsGenerating(true);
    
    setTimeout(() => {
      const selectedType = contractTypes.find(t => t.id === activeExample) || contractTypes[0];
      setContractContent(defaultContract);
      setCurrentContractType(selectedType.title);
      
      const newContract = {
        id: Date.now(),
        title: selectedType.title,
        Icon: selectedType.icon,
        subtitle: selectedType.subtitle,
        category: selectedType.category,
        content: defaultContract,
        timestamp: new Date()
      };
      
      setGeneratedContracts(prev => [newContract, ...prev.slice(0, 2)]);
      setIsGenerating(false);
      setShowModal(true);
    }, 2000);
  };

  const handleTypeSelect = (type) => {
    setActiveExample(type.id);
    setUserPrompt(type.prompt);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(contractContent);
  };

  const handleDownload = () => {
    const blob = new Blob([contractContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contract_${Date.now()}.txt`;
    a.click();
  };

  return (
    <>
      <div className="flex flex-col" style={{ 
        background: colors.white, 
        fontFamily: fontStyles.fontFamily,
        height: '100%',
        minHeight: '600px'
      }}>
        {/* Main Interface */}
        <div className="flex flex-col h-full">
          
          {/* Contract Type Selector - Professional Grid */}
          <div className="px-6 py-5" style={{ backgroundColor: colors.gray50 }}>
            <div className="grid grid-cols-4 gap-3">
              {contractTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <motion.button
                    key={type.id}
                    onClick={() => handleTypeSelect(type)}
                    className="relative p-4 rounded-xl border text-left transition-all"
                    style={{ 
                      borderColor: activeExample === type.id ? colors.accent : colors.border,
                      backgroundColor: colors.white,
                      boxShadow: activeExample === type.id 
                        ? '0 4px 12px rgba(30, 64, 175, 0.15), 0 0 0 2px rgba(30, 64, 175, 0.2)' 
                        : '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                           style={{ 
                             backgroundColor: activeExample === type.id ? colors.accent : colors.accentLight
                           }}>
                        <Icon size={20} style={{ 
                          color: activeExample === type.id ? colors.white : colors.accent 
                        }} />
                      </div>
                      {activeExample === type.id && (
                        <div className="w-2 h-2 rounded-full" 
                             style={{ backgroundColor: colors.success }} />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm mb-1" 
                           style={{ color: colors.gray900, ...fontStyles.medium }}>
                        {type.title}
                      </div>
                      <div className="text-xs" style={{ color: colors.gray500, ...fontStyles.body }}>
                        {type.subtitle}
                      </div>
                    </div>
                    <div className="mt-2 text-xs" 
                         style={{ 
                           color: colors.gray400,
                           textTransform: 'uppercase',
                           letterSpacing: '0.05em',
                           fontSize: '10px'
                         }}>
                      {type.category}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Input Section - Streamlined */}
          <div className="px-6 py-4 border-b" style={{ borderColor: colors.border }}>
            <div className="flex items-start space-x-4">
              <div className="flex-1">
                <textarea
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  placeholder="Describe your contract requirements in detail..."
                  className="w-full p-4 border rounded-xl resize-none focus:outline-none focus:ring-2 transition-all text-sm"
                  style={{ 
                    borderColor: colors.border,
                    backgroundColor: colors.white,
                    color: colors.gray900,
                    height: '100px',
                    ...fontStyles.body
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.accent;
                    e.target.style.boxShadow = `0 0 0 3px ${colors.accentLight}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <motion.button
                onClick={generateContract}
                disabled={!userPrompt.trim() || isGenerating}
                className="px-6 py-4 rounded-xl font-medium transition-all flex items-center text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  background: userPrompt.trim() && !isGenerating 
                    ? `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`
                    : colors.gray300,
                  boxShadow: userPrompt.trim() && !isGenerating ? '0 4px 14px rgba(30, 64, 175, 0.3)' : 'none',
                  minWidth: '160px',
                  ...fontStyles.medium
                }}
                whileHover={{ scale: userPrompt.trim() && !isGenerating ? 1.02 : 1 }}
                whileTap={{ scale: userPrompt.trim() && !isGenerating ? 0.98 : 1 }}
              >
                {isGenerating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Zap size={18} className="mr-2" />
                    Generate
                  </>
                )}
              </motion.button>
            </div>
          </div>

          {/* Generated Contracts Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {isGenerating ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <motion.div 
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: colors.accentLight }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <FileText size={32} style={{ color: colors.accent }} />
                  </motion.div>
                  <div className="text-base font-medium mb-2" style={{ color: colors.gray700, ...fontStyles.medium }}>
                    Generating your contract...
                  </div>
                  <div className="text-sm mb-6" style={{ color: colors.gray500, ...fontStyles.body }}>
                    AI is analyzing requirements and creating your document
                  </div>
                  
                  <div className="w-64 mx-auto">
                    <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.gray200 }}>
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: `linear-gradient(90deg, ${colors.accent} 0%, ${colors.accentHover} 100%)` }}
                        initial={{ width: '0%' }}
                        animate={{ width: '100%' }}
                        transition={{ duration: 2, ease: "linear" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : generatedContracts.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-medium" style={{ color: colors.gray900, ...fontStyles.medium }}>
                      Recent Contracts
                    </h3>
                    <p className="text-xs mt-1" style={{ color: colors.gray500 }}>
                      Click any contract to view full document
                    </p>
                  </div>
                  <div className="flex items-center text-xs px-3 py-1.5 rounded-full" 
                       style={{ backgroundColor: colors.successLight, color: colors.success }}>
                    <CheckCircle size={14} className="mr-1.5" />
                    {generatedContracts.length} generated
                  </div>
                </div>
                
                <div className="space-y-3">
                  {generatedContracts.map((contract, index) => {
                    const Icon = contract.Icon;
                    return (
                      <motion.div
                        key={contract.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="group"
                      >
                        <div
                          className="p-4 rounded-xl border cursor-pointer hover:shadow-lg transition-all"
                          style={{ 
                            borderColor: colors.border,
                            backgroundColor: colors.white,
                            background: 'linear-gradient(to right, white 0%, #fafbfc 100%)'
                          }}
                          onClick={() => {
                            setContractContent(contract.content);
                            setCurrentContractType(contract.title);
                            setShowModal(true);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4"
                                   style={{ backgroundColor: colors.gray50 }}>
                                <Icon size={24} style={{ color: colors.accent }} />
                              </div>
                              <div>
                                <div className="font-medium text-base mb-1" 
                                     style={{ color: colors.gray900, ...fontStyles.medium }}>
                                  {contract.title}
                                </div>
                                <div className="text-sm" style={{ color: colors.gray500 }}>
                                  {contract.subtitle}
                                </div>
                                <div className="flex items-center mt-1 text-xs" style={{ color: colors.gray400 }}>
                                  <Clock size={12} className="mr-1" />
                                  {new Date(contract.timestamp).toLocaleTimeString([], { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </div>
                              </div>
                            </div>
                            <motion.div 
                              className="flex items-center px-4 py-2 rounded-lg text-sm font-medium"
                              style={{ 
                                backgroundColor: colors.accentLight,
                                color: colors.accent,
                                ...fontStyles.medium
                              }}
                              whileHover={{ scale: 1.05 }}
                            >
                              <Eye size={16} className="mr-2" />
                              View
                            </motion.div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                       style={{ backgroundColor: colors.gray100 }}>
                    <FileText size={28} style={{ color: colors.gray400 }} />
                  </div>
                  <div className="text-base font-medium mb-2" style={{ color: colors.gray700, ...fontStyles.medium }}>
                    Select a contract type to begin
                  </div>
                  <div className="text-sm" style={{ color: colors.gray500, ...fontStyles.body }}>
                    Choose from the templates above or customize your requirements
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Status Bar */}
          <div className="px-6 py-3 border-t flex items-center justify-center space-x-8" 
               style={{ borderColor: colors.border, backgroundColor: colors.gray50 }}>
            <div className="flex items-center text-sm">
              <Zap size={16} className="mr-2" style={{ color: colors.accent }} />
              <span style={{ color: colors.gray600, ...fontStyles.body }}>
                Instant generation
              </span>
            </div>
            <div className="flex items-center text-sm">
              <Shield size={16} className="mr-2" style={{ color: colors.success }} />
              <span style={{ color: colors.gray600, ...fontStyles.body }}>
                Legally compliant
              </span>
            </div>
            <div className="flex items-center text-sm">
              <FileCheck size={16} className="mr-2" style={{ color: colors.accent }} />
              <span style={{ color: colors.gray600, ...fontStyles.body }}>
                Professional templates
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contract Modal */}
      <ContractModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        contractContent={contractContent}
        contractType={currentContractType}
        onCopy={handleCopy}
        onDownload={handleDownload}
      />
    </>
  );
};

// Main PreSignatureDemo component
export const PreSignatureDemo = () => {
  return <ModernContractGenerationDemo />;
};